import {
  surveyOutline,
  surveyQuestionNumber,
  visibleSurveyQuestions,
  type SurveySection,
} from "../../../../../shared/survey-questions";
import { AppModal, Button, DateFormField, Form } from "@/components/ui";
import {
  useMatterSurveyAnswerSession,
  useMatterSurveyAnswers,
  useReopenMatterSurvey,
} from "@/lib/data/react-query/matters/matter-survey";
import type {
  MatterSurvey,
  MatterSurveyAnswerReview,
  MatterSurveyQuestionDefinition,
} from "@/lib/data/requests/matters/matter-survey";
import type { APIError } from "@/lib/types/APIResponse";
import { queryKeys } from "@/lib/data/static/query-keys";
import { formatDateTime } from "@/lib/utils/format";
import { useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { VirtualSurveyQuestionList } from "@/components/features/surveys/VirtualSurveyQuestionList";
import type { MatterSurveyAction } from "./MatterSurveyActionDialog";
import { SurveyAnswerField } from "./MatterSurveyAnswerField";
import { useStaffSurveyDraft } from "./useStaffSurveyDraft";

function formatSurveyAnswer(
  question: Pick<MatterSurveyQuestionDefinition, "type" | "options">,
  value: string | string[] | null | undefined,
) {
  if (
    value == null ||
    value === "" ||
    (Array.isArray(value) && !value.length)
  ) {
    return "Not answered";
  }
  if (question.type === "multiple_choice" || question.type === "checkboxes") {
    const labels = new Map(
      question.options.map((option) => [option.id, option.label]),
    );
    return (Array.isArray(value) ? value : [value])
      .map((id) => labels.get(id) ?? id)
      .join(", ");
  }
  if (question.type === "datetime" && typeof value === "string") {
    return formatDateTime(value, false) ?? value;
  }
  return Array.isArray(value) ? value.join(", ") : value;
}

function displayAnswer(
  question: MatterSurveyAnswerReview["questions"][number],
) {
  return formatSurveyAnswer(question, question.answer?.value);
}

function hiddenQuestionReason(
  parent: MatterSurveyAnswerReview["questions"][number] | undefined,
  parentLabel: string,
  parentIsVisible: boolean,
  completed: boolean,
) {
  if (!parent) return "Not shown because its condition was not met.";
  if (!parentIsVisible) {
    return `Not shown because question ${parentLabel} was not shown.`;
  }

  const answer = parent.answer?.value;
  const selected = Array.isArray(answer) ? answer : answer ? [answer] : [];
  if (!selected.length) {
    return `Not shown because question ${parentLabel} ${completed ? "was not" : "has not been"} answered.`;
  }

  const optionLabels = new Map(
    parent.options.map((option) => [option.id, option.label]),
  );
  const response = selected
    .map((id) => `“${optionLabels.get(id) ?? id}”`)
    .join(" and ");
  return `Not shown because question ${parentLabel} was answered ${response}.`;
}

type ReviewQuestion = MatterSurveyAnswerReview["questions"][number];

function SurveySectionHeading({
  question,
  questions,
  sections,
}: {
  question: { id: string; sectionId?: string | null };
  questions: { id: string; sectionId?: string | null }[];
  sections?: SurveySection[];
}) {
  if (
    !question.sectionId ||
    questions.find((item) => item.sectionId === question.sectionId)?.id !==
      question.id
  )
    return null;
  const section = sections?.find((item) => item.id === question.sectionId);
  if (!section) return null;
  return (
    <div className="mb-4 border-b pb-3">
      <h3 className="font-semibold">{section.title}</h3>
      {section.description ? (
        <p className="text-muted-foreground text-sm">{section.description}</p>
      ) : null}
    </div>
  );
}

function ReviewQuestionRow({
  question,
  questions,
  outline,
  visibleIds,
  completed,
  baseDepth = 0,
}: {
  question: ReviewQuestion;
  questions: ReviewQuestion[];
  outline: ReturnType<typeof surveyOutline>;
  visibleIds: Set<string>;
  completed: boolean;
  baseDepth?: number;
}) {
  const condition = question.displayWhen;
  const parent = condition
    ? questions.find((candidate) => candidate.id === condition.questionId)
    : undefined;
  const triggerAnswers = parent?.options
    .filter((option) => condition?.optionIds.includes(option.id))
    .map((option) => `“${option.label}”`);
  const { depth, label } = outline.get(question.id);

  return (
    <div
      className="border-border border-b py-4"
      style={{
        paddingInlineStart: `${Math.max(Math.min(depth, 4) - baseDepth, 0) * 24}px`,
      }}
    >
      <p className="text-foreground font-medium">
        {surveyQuestionNumber(label)} {question.prompt}
      </p>
      {parent && triggerAnswers?.length ? (
        <p className="text-muted-foreground mt-1 text-sm">
          Shown when question {outline.get(parent.id).label} is{" "}
          {triggerAnswers.join(" or ")}
        </p>
      ) : null}
      <p className="text-muted-foreground mt-2 whitespace-pre-wrap">
        {visibleIds.has(question.id)
          ? displayAnswer(question)
          : hiddenQuestionReason(
              parent,
              parent ? outline.get(parent.id).label : "",
              parent ? visibleIds.has(parent.id) : false,
              completed,
            )}
      </p>
    </div>
  );
}

function localDate(value: Date) {
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
}

function ActiveMatterSurveyDialog({
  survey,
  startFilling,
  canFill,
  canDelete,
  onAction,
  onClose,
}: {
  survey: MatterSurvey;
  startFilling: boolean;
  canFill: boolean;
  canDelete: boolean;
  onAction: (action: MatterSurveyAction) => void;
  onClose: () => void;
}) {
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(
    null,
  );
  const [mode, setMode] = useState<"preview" | "fill" | "reopen">(
    startFilling ? "fill" : "preview",
  );
  const [reloadKey, setReloadKey] = useState(0);
  const [error, setError] = useState("");
  const [closing, setClosing] = useState(false);
  const [showNonApplicable, setShowNonApplicable] = useState(false);
  const reducedMotion = useReducedMotion();
  const queryClient = useQueryClient();
  const reopen = useReopenMatterSurvey(survey.matterId);
  const expiryDate = new Date(survey.expiresAt);
  const initialExpiry =
    expiryDate > new Date() ? expiryDate : new Date(Date.now() + 86400000);
  const expiryForm = useForm<{ expiresAt: string }>({
    defaultValues: { expiresAt: localDate(initialExpiry) },
  });
  const canCancel =
    ["pending", "in_progress", "reopened"].includes(survey.status) &&
    !survey.cancelledAt &&
    new Date(survey.expiresAt) >= new Date();
  const canReopen =
    canFill &&
    survey.status === "completed" &&
    Boolean(survey.latestSubmissionId && survey.latestSubmissionVersion);
  const results = useMatterSurveyAnswers(survey.id, mode === "preview");
  const session = useMatterSurveyAnswerSession(survey.id, canFill && canCancel);
  const form = session.data?.data ?? null;
  const draft = useStaffSurveyDraft(form, reloadKey);
  const orderedQuestions = [...(form?.questions ?? [])].sort(
    (a, b) => a.position - b.position,
  );
  const outline = surveyOutline(orderedQuestions);
  const questions = visibleSurveyQuestions(orderedQuestions, draft.answers);
  const reviewQuestions = results.data?.data.questions ?? [];
  const reviewOutline = surveyOutline(reviewQuestions);
  const visibleReviewIds = new Set(
    visibleSurveyQuestions(
      reviewQuestions,
      Object.fromEntries(reviewQuestions.map((q) => [q.id, q.answer?.value])),
    ).map((question) => question.id),
  );
  const nonApplicableQuestions = reviewQuestions.filter(
    (question) => !visibleReviewIds.has(question.id),
  );
  const nonApplicableDepth = nonApplicableQuestions.length
    ? Math.min(
        4,
        ...nonApplicableQuestions.map(
          (question) => reviewOutline.get(question.id).depth,
        ),
      )
    : 0;
  const displayedReviewQuestions =
    survey.status === "completed"
      ? reviewQuestions.filter((question) => visibleReviewIds.has(question.id))
      : reviewQuestions;
  const dateFormatter = new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
  });
  const dateLabel =
    survey.status === "completed" && results.data?.data.submittedAt
      ? `Submitted ${dateFormatter.format(new Date(results.data.data.submittedAt))}`
      : `Expires ${dateFormatter.format(new Date(survey.expiresAt))}`;

  const closeSafely = async () => {
    if (closing) return;
    if (!draft.ready) {
      onClose();
      return;
    }
    setClosing(true);
    setError("");
    try {
      await draft.flush();
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matterSurvey, survey.id],
        refetchType: "none",
      });
      await queryClient.invalidateQueries({
        queryKey: [queryKeys.matterSurveys, survey.matterId],
      });
      onClose();
    } catch {
      setError("Your answers could not be saved. Retry before closing.");
      setClosing(false);
    }
  };

  const confirmReopen = async () => {
    if (!survey.latestSubmissionId || !survey.latestSubmissionVersion) {
      setError("Reload this survey before reopening it.");
      return;
    }
    const dateValue = expiryForm.getValues("expiresAt");
    const deadline = new Date(`${dateValue}T23:59:59.999`);
    if (
      !dateValue ||
      Number.isNaN(deadline.getTime()) ||
      deadline <= new Date() ||
      deadline < expiryDate
    ) {
      setError(
        "Choose a future expiry date that does not shorten the current deadline.",
      );
      return;
    }
    setError("");
    try {
      await reopen.mutateAsync({
        surveyId: survey.id,
        expectedSubmissionId: survey.latestSubmissionId,
        expectedSubmissionVersion: survey.latestSubmissionVersion,
        expiresAt: deadline.toISOString(),
      });
      toast.success(
        "Survey reopened. The previous answers are ready for review.",
      );
      onClose();
    } catch (cause) {
      const response = isAxiosError(cause)
        ? (cause.response?.data as Partial<APIError> | undefined)
        : undefined;
      toast.error(
        response?.meta?.userMsg || "Could not reopen the survey. Try again.",
      );
    }
  };

  const reloadAnswers = () => {
    void session.refetch().then((latest) => {
      if (latest.data) {
        setReloadKey((current) => current + 1);
        setError("");
        setMode("fill");
      } else {
        toast.error("Could not reload the latest answers. Try again.");
      }
    });
  };

  const footer =
    mode === "preview" ? (
      canDelete || (canFill && canCancel) || canReopen ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            {canDelete && canCancel ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  onAction({ kind: "cancel", survey });
                }}
              >
                Cancel survey
              </Button>
            ) : null}
            {canDelete ? (
              <Button
                type="button"
                variant="destructive"
                onClick={() => {
                  onAction({ kind: "delete", survey });
                }}
              >
                Delete
              </Button>
            ) : null}
          </div>
          {canFill && canCancel ? (
            <Button
              type="button"
              onClick={() => {
                setMode("fill");
              }}
            >
              Fill for client
            </Button>
          ) : null}
          {canReopen ? (
            <Button
              type="button"
              onClick={() => {
                setMode("reopen");
              }}
            >
              Reopen survey
            </Button>
          ) : null}
        </div>
      ) : null
    ) : mode === "reopen" ? (
      <div className="flex w-full justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          disabled={reopen.isPending}
          onClick={() => {
            setError("");
            setMode("preview");
          }}
        >
          Cancel
        </Button>
        <Button
          type="button"
          disabled={reopen.isPending}
          onClick={() => void confirmReopen()}
        >
          {reopen.isPending ? "Reopening..." : "Reopen survey"}
        </Button>
      </div>
    ) : (
      <div className="flex w-full justify-end">
        <Button
          type="button"
          disabled={!draft.ready || closing || draft.reloadRequired}
          onClick={() => void closeSafely()}
        >
          {closing ? "Saving..." : "Done"}
        </Button>
      </div>
    );

  return (
    <AppModal
      open
      onOpenChange={(open) => {
        if (!open) {
          if (mode === "preview") onClose();
          else void closeSafely();
        }
      }}
      title={mode === "reopen" ? "Reopen survey" : survey.title}
      description={mode === "reopen" ? survey.title : dateLabel}
      size="2xl"
      bodyContained
      className={mode === "reopen" ? undefined : "sm:min-h-[36rem]"}
      footer={footer}
      loading={
        mode === "preview"
          ? results.isPending
          : mode === "reopen"
            ? false
            : !session.isError && (session.isPending || !draft.ready)
      }
    >
      <div
        ref={setScrollElement}
        className="min-h-0 flex-1 overflow-y-auto px-6 py-5"
      >
        {mode === "reopen" ? (
          <div className="space-y-4 py-1">
            <p className="text-muted-foreground text-sm">
              The previous submission stays on record. The client can review and
              submit updated answers.
            </p>
            <Form {...expiryForm}>
              <DateFormField
                name="expiresAt"
                register={expiryForm.register("expiresAt")}
                label="New expiry date"
                minDate={new Date()}
                clearable={false}
              />
            </Form>
            {error ? (
              <p role="alert" className="text-destructive text-sm">
                {error}
              </p>
            ) : null}
          </div>
        ) : mode === "preview" ? (
          <div className="space-y-4">
            <p className="text-muted-foreground text-sm">{dateLabel}</p>
            {results.data?.data.acknowledgement.acknowledgementFullName ? (
              <p className="text-muted-foreground text-sm">
                Acknowledged by{" "}
                {results.data.data.acknowledgement.acknowledgementFullName}
              </p>
            ) : null}
            {results.isPending ? (
              <div className="min-h-40" />
            ) : results.isError ? (
              <div className="space-y-3 py-5">
                <p role="alert" className="text-destructive text-sm">
                  Could not load survey results.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => void results.refetch()}
                >
                  Retry
                </Button>
              </div>
            ) : (
              <>
                <VirtualSurveyQuestionList
                  key="review"
                  items={displayedReviewQuestions}
                  getKey={(question) => question.id}
                  scrollElement={scrollElement}
                  renderItem={(question) => (
                    <div>
                      <SurveySectionHeading
                        question={question}
                        questions={displayedReviewQuestions}
                        sections={results.data.data.sections}
                      />
                      <ReviewQuestionRow
                        key={question.id}
                        question={question}
                        questions={reviewQuestions}
                        outline={reviewOutline}
                        visibleIds={visibleReviewIds}
                        completed={survey.status === "completed"}
                      />
                    </div>
                  )}
                />
                {survey.status === "completed" &&
                nonApplicableQuestions.length ? (
                  <div
                    className="border-border rounded-lg border"
                    style={{
                      marginInlineStart: `${nonApplicableDepth * 24}px`,
                    }}
                  >
                    <button
                      type="button"
                      className="hover:bg-muted flex w-full cursor-pointer items-center gap-1 px-4 py-3 text-left text-sm font-medium"
                      aria-expanded={showNonApplicable}
                      onClick={() => {
                        setShowNonApplicable((open) => !open);
                      }}
                    >
                      <ChevronDown
                        className={`size-4 shrink-0 transition-transform duration-200 motion-reduce:transition-none ${showNonApplicable ? "" : "-rotate-90"}`}
                        aria-hidden
                      />
                      {nonApplicableQuestions.length} conditional{" "}
                      {nonApplicableQuestions.length === 1
                        ? "question"
                        : "questions"}{" "}
                      not applicable
                    </button>
                    <AnimatePresence initial={false}>
                      {showNonApplicable ? (
                        <motion.div
                          className="overflow-hidden"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: reducedMotion ? 0 : 0.28 }}
                        >
                          <div className="border-border border-t px-4">
                            <VirtualSurveyQuestionList
                              items={nonApplicableQuestions}
                              getKey={(question) => question.id}
                              scrollElement={scrollElement}
                              label="Non-applicable survey questions"
                              renderItem={(question) => (
                                <div>
                                  <SurveySectionHeading
                                    question={question}
                                    questions={nonApplicableQuestions}
                                    sections={results.data.data.sections}
                                  />
                                  <ReviewQuestionRow
                                    key={question.id}
                                    question={question}
                                    questions={reviewQuestions}
                                    outline={reviewOutline}
                                    visibleIds={visibleReviewIds}
                                    completed
                                    baseDepth={nonApplicableDepth}
                                  />
                                </div>
                              )}
                            />
                          </div>
                        </motion.div>
                      ) : null}
                    </AnimatePresence>
                  </div>
                ) : null}
              </>
            )}
          </div>
        ) : session.isError || form?.readOnly ? (
          <div className="space-y-3 py-5">
            <p role="alert" className="text-destructive text-sm">
              {form?.readOnly
                ? form.unavailableReason === "expired"
                  ? "This survey has expired."
                  : form.unavailableReason === "cancelled"
                    ? "This survey was cancelled."
                    : "This survey has already been completed."
                : "Could not load this survey for completion."}
            </p>
            <Button type="button" variant="outline" onClick={reloadAnswers}>
              Retry
            </Button>
          </div>
        ) : session.isPending || !form || !draft.ready ? (
          <div className="min-h-40" />
        ) : (
          <div className="space-y-5">
            <div className="space-y-5">
              <p className="text-muted-foreground text-sm">
                Draft answers are saved for the client to review and submit.
              </p>
              <VirtualSurveyQuestionList
                key="fill"
                items={questions}
                getKey={(question) => question.id}
                scrollElement={scrollElement}
                rowClassName="pb-5"
                estimateSize={140}
                renderItem={(question) => (
                  <div>
                    <SurveySectionHeading
                      question={question}
                      questions={questions}
                      sections={form.sections}
                    />
                    <SurveyAnswerField
                      key={question.id}
                      question={question}
                      number={outline.get(question.id).label}
                      depth={outline.get(question.id).depth}
                      value={draft.answers[question.id] ?? null}
                      disabled={closing || draft.reloadRequired}
                      saveStatus={draft.saveStatus[question.id]}
                      error={false}
                      onChange={(value) => {
                        draft.updateAnswer(
                          question.id,
                          value,
                          question.type === "short_answer" ||
                            question.type === "long_answer"
                            ? 500
                            : 0,
                        );
                        setError("");
                      }}
                    />
                  </div>
                )}
              />
            </div>
            {error || draft.saveError ? (
              <div role="alert" className="text-destructive space-y-2 text-sm">
                <p>{error || draft.saveError}</p>
                {draft.reloadRequired ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={reloadAnswers}
                  >
                    Reload latest answers
                  </Button>
                ) : draft.saveError ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => void draft.flush().catch(() => {})}
                  >
                    Retry save
                  </Button>
                ) : null}
                {draft.saveError || draft.reloadRequired ? (
                  <Button type="button" variant="ghost" onClick={onClose}>
                    Close without saving
                  </Button>
                ) : null}
              </div>
            ) : null}
          </div>
        )}
      </div>
    </AppModal>
  );
}

export function MatterSurveyResultsDialog({
  survey,
  startFilling = false,
  canFill,
  canDelete,
  onAction,
  onOpenChange,
}: {
  survey: MatterSurvey | null;
  startFilling?: boolean;
  canFill: boolean;
  canDelete: boolean;
  onAction: (action: MatterSurveyAction) => void;
  onOpenChange: (open: boolean) => void;
}) {
  if (!survey) return null;
  return (
    <ActiveMatterSurveyDialog
      key={survey.id}
      survey={survey}
      startFilling={startFilling}
      canFill={canFill}
      canDelete={canDelete}
      onAction={onAction}
      onClose={() => {
        onOpenChange(false);
      }}
    />
  );
}
